import { useCallback, useEffect, useRef, useState } from "react";
import {
  clampRowHeight,
  loadRowHeights,
  saveRowHeights,
  type RowHeightMap,
} from "@/lib/scheduleRowHeights";

export interface ScheduleRowHeights {
  getRowHeight: (rowKey: string, defaultHeight: number) => number;
  setRowHeight: (rowKey: string, height: number) => void;
  resetRowHeight: (rowKey: string) => void;
}

/**
 * Per-row schedule heights, persisted per briefing in localStorage so they
 * also work on the unauthenticated client-share page. Shared by the schedule
 * creator and the read-only timeline.
 */
export function useScheduleRowHeights(briefingId: string): ScheduleRowHeights {
  const [heights, setHeights] = useState<RowHeightMap>(() => loadRowHeights(briefingId));
  // Mirror of `heights` so updates during a drag (which arrive faster than
  // re-renders) compose correctly while keeping the setState updater pure —
  // persisting inside the updater would double-write under StrictMode.
  const heightsRef = useRef(heights);

  useEffect(() => {
    const loaded = loadRowHeights(briefingId);
    heightsRef.current = loaded;
    setHeights(loaded);
  }, [briefingId]);

  const applyHeights = useCallback(
    (next: RowHeightMap) => {
      heightsRef.current = next;
      setHeights(next);
      saveRowHeights(briefingId, next);
    },
    [briefingId],
  );

  const getRowHeight = useCallback(
    (rowKey: string, defaultHeight: number) => heights[rowKey] ?? defaultHeight,
    [heights],
  );

  const setRowHeight = useCallback(
    (rowKey: string, height: number) => {
      applyHeights({ ...heightsRef.current, [rowKey]: clampRowHeight(height) });
    },
    [applyHeights],
  );

  const resetRowHeight = useCallback(
    (rowKey: string) => {
      if (!(rowKey in heightsRef.current)) return;
      const next = { ...heightsRef.current };
      delete next[rowKey];
      applyHeights(next);
    },
    [applyHeights],
  );

  return { getRowHeight, setRowHeight, resetRowHeight };
}
